import React, { useEffect, useRef, useState } from "react";
import { useAuth, type User } from "../contexts/AuthContext";
import configService from "../services/configService";
import { trackingService } from "../services/trackingService";
import { TrackingEvents } from "../services/trackingEvents";

interface GoogleSignInButtonProps {
  oauthQuery?: string;
  /** Runs when Google click intent is detected, before the click event is tracked. */
  onClickIntent?: () => void;
  /** Runs after a successful, non-redirected sign-in, with the signed-in user. The share gate chains its own steps here. */
  onSuccess?: (user: User | undefined) => void | Promise<void>;
  /** True while a Google sign-in is in flight, false once it settles (not after a redirect). Lets a caller lock its other ways in. */
  onBusyChange?: (busy: boolean) => void;
  /** Google's button label. The auth pages say "Sign in with Google"; the share gate says "Continue with Google". */
  text?: "signin_with" | "signup_with" | "continue_with";
  /** Google's colour theme; "filled_black" sits back on a dark ground such as the homepage hero. */
  theme?: "outline" | "filled_blue" | "filled_black";
}

const GoogleSignInButton: React.FC<GoogleSignInButtonProps> = ({ oauthQuery, onClickIntent, onSuccess, onBusyChange, text = "signin_with", theme = "outline" }) => {
  const buttonRef = useRef<HTMLDivElement | null>(null);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const { signInWithGoogle } = useAuth();
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [containerWidth, setContainerWidth] = useState<number>(0);

  const isEnabled = configService.isGoogleSignInEnabled();

  // Read through a ref so a caller passing a fresh callback each render (the
  // share gate's changes with every keystroke) never re-initializes Google
  // Identity Services or re-renders its button.
  const onSuccessRef = useRef(onSuccess);
  onSuccessRef.current = onSuccess;
  const onClickIntentRef = useRef(onClickIntent);
  onClickIntentRef.current = onClickIntent;
  const onBusyChangeRef = useRef(onBusyChange);
  onBusyChangeRef.current = onBusyChange;
  // One sign-in at a time: Google's button stays clickable while ours is in
  // flight, and a second credential would start a second auth transition
  // (and, for callers like the share gate, a second completion chain).
  const inFlightRef = useRef(false);

  // ResizeObserver to track container width (debounced to prevent flickering)
  useEffect(() => {
    if (!containerRef.current) return;

    // Fallback for environments without ResizeObserver (Jest, SSR, old browsers)
    if (typeof ResizeObserver === "undefined") {
      setContainerWidth(384);
      return;
    }

    let timeoutId: NodeJS.Timeout;
    const observer = new ResizeObserver(entries => {
      clearTimeout(timeoutId);
      timeoutId = setTimeout(() => {
        const width = Math.floor(entries[0].contentRect.width);
        if (width > 0) setContainerWidth(width);
      }, 150);
    });
    observer.observe(containerRef.current);
    return () => {
      clearTimeout(timeoutId);
      observer.disconnect();
    };
  }, []);

  useEffect(() => {
    // Skip initialization if feature flag is disabled
    if (!isEnabled) {
      return;
    }

    // Wait for container width to be measured before rendering
    if (containerWidth === 0) return;

    let cancelled = false;
    const clientId = configService.getGoogleClientId();
    if (!clientId) {
      setError("Google sign-in is not configured.");
      return;
    }

    const initialize = () => {
      if (cancelled) return;
      const googleId = window.google?.accounts?.id;
      if (!googleId) {
        setError("Google sign-in failed to load.");
        return;
      }

      googleId.initialize({
        client_id: clientId,
        callback: async (response) => {
          // NOTE: This callback runs only AFTER Google returns a credential
          // payload. Click-intent is captured upstream by the effect below
          // (primary: window.blur + activeElement-inside-buttonRef; fallback:
          // pointerdown). We only emit credential-received outcomes here
          // (success / missing_credential) -- re-emitting
          // AUTH_GOOGLE_SSO_CLICK would double-count handoffs and still
          // miss canceled attempts.
          if (!response?.credential) {
            setError("Google sign-in failed.");
            trackingService.track(TrackingEvents.AUTH_GOOGLE_SSO_ERROR, {
              reason: "missing_credential",
            });
            return;
          }

          if (inFlightRef.current) return;
          inFlightRef.current = true;
          onBusyChangeRef.current?.(true);
          setIsLoading(true);
          setError(null);
          let redirected = false;
          try {
            const result = oauthQuery === undefined
              ? await signInWithGoogle(response.credential)
              : await signInWithGoogle(response.credential, undefined, oauthQuery);
            // Leaving the page: stay loading and locked until it goes.
            if (result.redirected) {
              redirected = true;
              return;
            }
            if (!result.success) {
              setError(result.message || "Google sign-in failed.");
            } else if (onSuccessRef.current) {
              await onSuccessRef.current(result.user);
            }
          } finally {
            if (!redirected) {
              inFlightRef.current = false;
              setIsLoading(false);
              onBusyChangeRef.current?.(false);
            }
          }
        },
      });

      if (buttonRef.current) {
        buttonRef.current.innerHTML = "";
        googleId.renderButton(buttonRef.current, {
          theme,
          size: "large",
          text,
          shape: "rectangular",
          width: String(Math.min(containerWidth, 400)),
        });
      }
    };

    if (window.google?.accounts?.id) {
      initialize();
      return;
    }

    const intervalId = window.setInterval(() => {
      if (window.google?.accounts?.id) {
        window.clearInterval(intervalId);
        initialize();
      }
    }, 100);

    return () => {
      cancelled = true;
      window.clearInterval(intervalId);
    };
  }, [signInWithGoogle, isEnabled, containerWidth, oauthQuery, text, theme]);

  // Emit AUTH_GOOGLE_SSO_CLICK on actual user intent, not inside Google's
  // credential callback (which only runs on successful handoff, so it
  // misses canceled attempts and pre-credential failures and therefore
  // overstates click -> success conversion).
  //
  // GIS `renderButton` renders the clickable surface inside a
  // cross-origin iframe served from accounts.google.com. Browsers do not
  // bubble pointerdown / mousedown / click events from a cross-origin
  // iframe up to the parent document, so a plain pointerdown listener on
  // `buttonRef` will never see the real click. Instead we detect click
  // intent via two complementary signals, gated on a single-fire lock
  // with a 1s reset so repeated click attempts (user cancels the modal,
  // tries again) still count:
  //
  //   1. `window.blur` + `document.activeElement` contained in
  //      `buttonRef`. When the user clicks the GIS iframe, the parent
  //      window loses focus and activeElement becomes the <iframe>
  //      element inside buttonRef. Checking containment filters out
  //      plain tab-switches / cmd-tab (activeElement stays on whatever
  //      had focus before, which is NOT inside buttonRef).
  //   2. Direct `pointerdown` on buttonRef with `capture: true`. This
  //      only fires in the edge case where GIS did NOT render its iframe
  //      (e.g. an adblocker or network failure swapped in a fallback
  //      surface inside buttonRef). Cheap insurance.
  //
  // The event payload includes a `detection` property so we can see
  // post-deploy what fraction of emissions came from each signal and
  // simplify if one path dominates.
  useEffect(() => {
    if (!isEnabled) return;
    const el = buttonRef.current;
    if (!el) return;

    let active = true;
    let fired = false;
    const emit = (detection: "blur" | "pointerdown") => {
      if (!active || fired) return;
      fired = true;
      onClickIntentRef.current?.();
      trackingService.track(TrackingEvents.AUTH_GOOGLE_SSO_CLICK, { detection });
      window.setTimeout(() => {
        fired = false;
      }, 1000);
    };

    const pointerHandler = () => emit("pointerdown");
    const blurHandler = () => {
      // activeElement may still be the previous element at the exact
      // moment blur fires; give the browser one frame to settle.
      window.requestAnimationFrame(() => {
        const active = document.activeElement;
        if (active && el.contains(active)) {
          emit("blur");
        }
      });
    };

    el.addEventListener("pointerdown", pointerHandler, { capture: true });
    window.addEventListener("blur", blurHandler);
    return () => {
      active = false;
      el.removeEventListener(
        "pointerdown",
        pointerHandler,
        { capture: true } as EventListenerOptions
      );
      window.removeEventListener("blur", blurHandler);
    };
  }, [isEnabled, containerWidth]);

  // Feature flag: hide button if Google Sign-In is disabled
  if (!isEnabled) {
    return null;
  }

  return (
    <div ref={containerRef} className="w-full">
      <div
        ref={buttonRef}
        aria-busy={isLoading || undefined}
        className={`flex justify-center w-full min-h-[40px] ${isLoading ? "pointer-events-none opacity-60" : ""}`}
      />
      {isLoading && (
        <div className="mt-3 text-center text-sm text-gray-500">
          Signing in with Google...
        </div>
      )}
      {error && (
        <div className="mt-3 bg-red-50 border border-red-200 rounded-lg p-3">
          <p className="text-sm text-red-600">{error}</p>
        </div>
      )}
    </div>
  );
};

export default GoogleSignInButton;
